import 'intersection-observer'
import '@mdi/font/css/materialdesignicons.css'
import * as Sentry from '@sentry/vue'
import { camelCase, upperFirst } from 'lodash'
import LogRocket from 'logrocket'
import moment from 'moment'
import PortalVue from 'portal-vue'
import vClickOutside from 'v-click-outside'
import 'vis-timeline/styles/vis-timeline-graph2d.css'
// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import VueRouter from 'vue-router'
import VueZoomer from 'vue-zoomer'
import VueVirtualScroller from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import App from './App.vue'
import { Auth0Plugin } from './auth'
import router from './router.js'
import store from './store'
import { VueQueryPlugin } from '@tanstack/vue-query'
import vuetify from './plugins/vuetify'
import { createPinia, PiniaVuePlugin } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import 'mapbox-gl/dist/mapbox-gl.css'
import VueApexCharts from 'vue-apexcharts'
import VueApplicationInsights from '@/plugins/VueApplicationInsights'
import { queryClient } from '@/plugins/vue-query'
import { getRuntimeConfig } from '@/runtime-config'
import { SET_CLIENT_VERSION } from '@/store/mutations'
import { bakedClientVersion } from '@/store/state'

moment.relativeTimeThreshold('ss', 5)

const runtimeConfig = getRuntimeConfig()
const clientVersion = bakedClientVersion
Vue.prototype.$clientVersion = clientVersion
store.commit(SET_CLIENT_VERSION, clientVersion)

/**
 * Globally register every Vs* design-system component. `eager: true` matches
 * the previous require.context behaviour (synchronous registration before
 * mount). The __tests__ exclusion is load-bearing: the old require.context
 * regex (/Vs[A-Z]\w+\.(vue|js)$/) could not match "VsButton.spec.js" because
 * \w+ does not span the dot, whereas this glob would — and an eager glob would
 * bundle those specs and their test-only dependencies into the production app.
 */
const visionComponents = import.meta.glob<Record<string, unknown>>(
  ['./components/vision/**/Vs[A-Z]*.{vue,js}', '!**/__tests__/**'],
  { eager: true },
)

Object.entries(visionComponents).forEach(([fileName, componentConfig]) => {
  // Get PascalCase name of component, regardless of folder depth
  const componentName = upperFirst(
    camelCase(
      fileName
        .split('/')
        .pop()!
        .replace(/\.\w+$/, ''),
    ),
  )

  // Look for the component options on `.default`, which will exist if the
  // component was exported with `export default`, otherwise fall back to the
  // module's root.
  Vue.component(componentName, (componentConfig.default ?? componentConfig) as never)
})

/**
 * @todo (vue3): Uses `process.env.*` instead of `import.meta.env` only because
 *       Jest cannot parse import.meta. Switch when tests move to Vitest; the
 *       `define` block in vite.config.mts is what keeps this working.
 */
const isProduction = process.env.NODE_ENV === 'production'

Vue.use(VueRouter)
Vue.use(vClickOutside)
Vue.use(PortalVue)
Vue.use(VueZoomer)
Vue.use(VueVirtualScroller)
Vue.use(VueQueryPlugin, { queryClient })
Vue.use(PiniaVuePlugin)

Vue.use(Auth0Plugin, {
  onRedirectCallback: (appState) => {
    if (appState && appState.targetUrl) {
      router.push(appState.targetUrl)
    }
  },
})

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

Vue.config.productionTip = false
Vue.config.performance = !isProduction

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const logRocketAppId = runtimeConfig.logRocketProject
if (isProduction && logRocketAppId) {
  LogRocket.init(logRocketAppId, {
    release: clientVersion,
  })
} else if (!logRocketAppId) {
  console.debug('Skipped LogRocket initialization due to missing app ID.')
}

if (isProduction) {
  Sentry.init({
    Vue,
    dsn: runtimeConfig.sentryDsn,
    release: clientVersion,
    integrations: [Sentry.browserTracingIntegration({ router })],
    // Sample performance transactions while keeping error capture unsampled.
    tracesSampleRate: 0.1,
  })
}

Vue.use(VueApplicationInsights, {
  router,
})

new Vue({
  render: (h) => h(App),
  router,
  store,
  vuetify,
  pinia,
}).$mount('#app')
